import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Feather, Heart, BookOpen, Send } from 'lucide-react';
import SEO from '../components/SEO';
import { LEGAL_INFO } from './LegalPages';

// =============== À PROPOS ===============
export const APropos = () => (
  <>
    <SEO
      title="À propos"
      description="Découvrez l'histoire et la mission de BcoolStore, magazine éditorial français dédié aux récits vécus."
      url="/a-propos"
    />
    <main className="max-w-3xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#c9a961] font-semibold mb-3">
          Qui sommes-nous
        </div>
        <h1
          className="text-[36px] md:text-[52px] font-bold text-[#1a1414] leading-[1.05] italic"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          À propos de <span className="text-[#8b2c3f]">BcoolStore</span>
        </h1>
        <div className="h-[3px] w-14 bg-[#c9a961] mt-5" />
      </div>

      <div
        className="text-[17px] leading-[1.75] text-[#1a1414] space-y-5"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        <p className="text-[20px] italic text-[#3a3232]">
          BcoolStore est un magazine éditorial français qui raconte les histoires
          qui donnent envie de tourner la page.
        </p>

        <p>
          Ici, on ne fait pas d&apos;actualité chaude. On raconte des <strong>récits vécus</strong>,
          des chroniques familiales, des confidences arrachées à l&apos;ordinaire. Des histoires
          où l&apos;on reconnaît un morceau de soi — dans une belle-mère qui humilie, un mari
          qui trahit, un parent qui n&apos;a pas su défendre, un inconnu qui sauve. Des histoires
          qui touchent parce qu&apos;elles ressemblent aux nôtres.
        </p>

        <h2
          className="text-[26px] font-bold text-[#1a1414] mt-10 mb-4 italic border-b border-[#c9a961]/40 pb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Notre promesse
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
          <div className="border border-[#e6ddd0] p-5">
            <Feather className="text-[#8b2c3f] mb-3" size={22} />
            <h3 className="font-bold text-[15px] uppercase tracking-[0.14em] text-[#1a1414] mb-2">
              Chaque mot pesé
            </h3>
            <p className="text-[14px] text-[#3a3232] leading-relaxed">
              Nos récits sont publiés lentement, corrigés soigneusement,
              relus plusieurs fois.
            </p>
          </div>
          <div className="border border-[#e6ddd0] p-5">
            <Heart className="text-[#8b2c3f] mb-3" size={22} />
            <h3 className="font-bold text-[15px] uppercase tracking-[0.14em] text-[#1a1414] mb-2">
              Sans voyeurisme
            </h3>
            <p className="text-[14px] text-[#3a3232] leading-relaxed">
              On raconte l&apos;émotion, pas le sensationnel.
              La pudeur reste notre boussole.
            </p>
          </div>
          <div className="border border-[#e6ddd0] p-5">
            <BookOpen className="text-[#8b2c3f] mb-3" size={22} />
            <h3 className="font-bold text-[15px] uppercase tracking-[0.14em] text-[#1a1414] mb-2">
              Toujours original
            </h3>
            <p className="text-[14px] text-[#3a3232] leading-relaxed">
              Aucun copier-coller, aucune histoire reprise ailleurs.
              Tout ici est né entre ces pages.
            </p>
          </div>
        </div>

        <h2
          className="text-[26px] font-bold text-[#1a1414] mt-10 mb-4 italic border-b border-[#c9a961]/40 pb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Qui écrit ?
        </h2>

        <p>
          BcoolStore est édité par <strong>{LEGAL_INFO.editorName}</strong> depuis
          la Manche, en Normandie. Un projet indépendant, sans capital risque, sans investisseur,
          juste une plume, une passion et un ordinateur.
        </p>

        <p>
          Certains récits sont écrits par la rédaction. D&apos;autres nous sont confiés
          par des lectrices et lecteurs qui souhaitent transmettre leur histoire —
          anonymement ou non. Toujours avec leur accord explicite, toujours avec soin.
        </p>

        <h2
          className="text-[26px] font-bold text-[#1a1414] mt-10 mb-4 italic border-b border-[#c9a961]/40 pb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Vous avez une histoire à partager ?
        </h2>

        <p>
          Nous lisons chaque récit reçu. Si votre histoire nous touche et respecte notre
          ligne éditoriale (relations humaines, dignité, résilience), elle pourra être
          publiée — retravaillée si besoin, sous pseudonyme si vous le demandez.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[12px] uppercase font-semibold tracking-[0.22em] px-6 py-3 transition-colors"
          >
            Nous contacter <ArrowRight size={14} />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 border border-[#1a1414] hover:bg-[#1a1414] hover:text-white text-[#1a1414] text-[12px] uppercase font-semibold tracking-[0.22em] px-6 py-3 transition-colors"
          >
            Lire les dernières histoires
          </Link>
        </div>
      </div>
    </main>
  </>
);

// =============== CONTACT ===============
export const Contact = () => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'ok' | 'err'

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.target;
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const subject = form.subject.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !message) {
      setStatus('err');
      return;
    }

    const mailto = `mailto:${LEGAL_INFO.editorEmail}?subject=${encodeURIComponent(
      subject || `Message de ${name}`
    )}&body=${encodeURIComponent(
      `De : ${name} <${email}>\n\n${message}\n\n---\nEnvoyé depuis bcoolstories.com`
    )}`;
    window.location.href = mailto;
    setStatus('ok');
    setTimeout(() => form.reset(), 500);
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Contactez la rédaction de BcoolStore par email. Nous lisons tous les messages."
        url="/contact"
      />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c9a961] font-semibold mb-3">
            Écrivez-nous
          </div>
          <h1
            className="text-[36px] md:text-[52px] font-bold text-[#1a1414] leading-[1.05] italic"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Nous <span className="text-[#8b2c3f]">contacter</span>
          </h1>
          <div className="h-[3px] w-14 bg-[#c9a961] mt-5" />
        </div>

        <p
          className="text-[18px] italic text-[#3a3232] mb-10 leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Une histoire à partager, une question, un retour sur nos récits ?
          Écrivez-nous — nous lisons chaque message.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-[#e6ddd0] p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] uppercase font-bold tracking-[0.16em] text-[#1a1414] mb-2">
                Votre nom <span className="text-[#8b2c3f]">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                className="w-full border border-[#e6ddd0] focus:border-[#8b2c3f] outline-none px-4 py-3 text-[15px]"
                placeholder="Prénom Nom"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase font-bold tracking-[0.16em] text-[#1a1414] mb-2">
                Email <span className="text-[#8b2c3f]">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full border border-[#e6ddd0] focus:border-[#8b2c3f] outline-none px-4 py-3 text-[15px]"
                placeholder="vous@email.fr"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase font-bold tracking-[0.16em] text-[#1a1414] mb-2">
              Sujet
            </label>
            <select
              name="subject"
              className="w-full border border-[#e6ddd0] focus:border-[#8b2c3f] outline-none px-4 py-3 text-[15px] bg-white"
            >
              <option>Partager une histoire vécue</option>
              <option>Une question sur un article</option>
              <option>Proposition de partenariat</option>
              <option>Signaler un problème sur le site</option>
              <option>Autre demande</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] uppercase font-bold tracking-[0.16em] text-[#1a1414] mb-2">
              Votre message <span className="text-[#8b2c3f]">*</span>
            </label>
            <textarea
              name="message"
              rows={8}
              required
              className="w-full border border-[#e6ddd0] focus:border-[#8b2c3f] outline-none px-4 py-3 text-[15px] leading-relaxed"
              placeholder="Racontez-nous…"
            />
          </div>

          {status === 'err' && (
            <p className="text-[13px] text-[#8b2c3f]">
              Merci de remplir tous les champs obligatoires.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={status === 'sending'}
              className="inline-flex items-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[12px] uppercase font-semibold tracking-[0.22em] px-6 py-3 transition-colors disabled:opacity-50"
            >
              <Send size={14} /> Envoyer mon message
            </button>
            <a
              href={`mailto:${LEGAL_INFO.editorEmail}`}
              className="inline-flex items-center gap-2 text-[13px] text-[#3a3232] hover:text-[#8b2c3f] transition-colors"
            >
              <Mail size={14} /> {LEGAL_INFO.editorEmail}
            </a>
          </div>
        </form>

        <p className="text-[12px] text-[#7a716c] mt-6 leading-relaxed">
          Le bouton ci-dessus ouvre votre application email par défaut avec votre message
          pré-rempli. Vous restez maître de l&apos;envoi.
        </p>
      </main>
    </>
  );
};
